import { GridFilterModel } from '@mui/x-data-grid-premium'
import { createContext, PropsWithChildren, useContext, useEffect, useRef } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { FilterModel } from '@omnea-app/packages/universal/saved-filters/validateFilterModel'
import { ValidUuidString } from '@omnea-app/packages/universal/string-utils/isUuid'
import { TableFilterReference } from '@omnea-app/packages/universal/tables/tableFilterReference'
import { useGetTableFiltersByFilter } from '../../table-filters/useTableFilters'
import { extractUserDefinedFilters, mergeSavedFilterWithUserFilters } from '../helpers/manageSavedAndUserFilters'

export type SavedFilter = {
  id: string
  filters: FilterModel
  name: string
  reference: TableFilterReference
  isPublic: boolean
  userId: string
  deletedAt: string | null
}

export interface SavedFilterContextValue {
  savedFilter: SavedFilter | null
  isLoadingSavedFilter: boolean
  reference: TableFilterReference | undefined
  onUnselectSavedFilter(): void
  // A table whose data source can't serve every stored view supplies this to mark those views
  // non-applicable in the picker; the returned string is the tooltip reason. Omitted = all selectable.
  getSavedFilterDisabledReason?: (filters: FilterModel) => string | null
}

function getInitialContextValue(): SavedFilterContextValue {
  return {
    savedFilter: null,
    isLoadingSavedFilter: false,
    reference: undefined,
    onUnselectSavedFilter: () => {},
  }
}

export const SavedFilterContext = createContext(getInitialContextValue())

export function useSavedFilterContext(): SavedFilterContextValue {
  return useContext(SavedFilterContext)
}
interface SavedFilterProviderProps extends PropsWithChildren {
  savedFilterId: ValidUuidString | null
  onUnselectSavedFilter(): void
  filterModel: GridFilterModel
  setFilterModel(filterModel: GridFilterModel): void
  reference: TableFilterReference
  getSavedFilterDisabledReason?: SavedFilterContextValue['getSavedFilterDisabledReason']
}

/**
 * This component is responsible for loading the saved filter from the DB and setting the filter model.
 * With the contents of the saved filter.
 *
 * It also handles clearing the saved filter from the URL when the filter model changes to something that is not a variation of the saved filter.
 */
export const SavedFilterProvider: React.FC<SavedFilterProviderProps> = ({
  children,
  savedFilterId,
  onUnselectSavedFilter,
  filterModel,
  setFilterModel,
  reference,
  getSavedFilterDisabledReason,
}) => {
  const { org } = useAuthenticatedUser()
  const storageKey = `lastAppliedSavedFilter_${reference}_${org.organisationId}`

  const { data: tableFilters, isLoading: isLoadingSavedFilter } = useGetTableFiltersByFilter(reference)
  const savedFilter = tableFilters?.find((filter) => filter?.id === savedFilterId) ?? null
  const lastAppliedFilterRef = useRef<string | null>(null)
  const unselectedSavedFilterIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (isLoadingSavedFilter) {
      return
    }

    if (!savedFilterId) {
      lastAppliedFilterRef.current = null
      unselectedSavedFilterIdRef.current = null
      return
    }

    if (savedFilter === null || getSavedFilterDisabledReason?.(savedFilter.filters)) {
      // `onUnselectSavedFilter` and `filterModel` are new every render, so without this an id the list
      // never resolves fires a fresh redirect on each one, and each cancels the one before it.
      if (unselectedSavedFilterIdRef.current === savedFilterId) return
      unselectedSavedFilterIdRef.current = savedFilterId
      onUnselectSavedFilter()
      return
    }

    if (lastAppliedFilterRef.current === savedFilterId) {
      return
    }

    // determine whether we are switching filters or applying from a new page load
    const currentSavedFilter =
      lastAppliedFilterRef.current !== null
        ? tableFilters?.find((filter) => filter?.id === lastAppliedFilterRef.current)
        : savedFilter
    const userDefined = extractUserDefinedFilters(filterModel, currentSavedFilter)
    const mergedFilterModel = mergeSavedFilterWithUserFilters(savedFilter, userDefined)
    setFilterModel(mergedFilterModel)

    lastAppliedFilterRef.current = savedFilterId
  }, [
    savedFilterId,
    isLoadingSavedFilter,
    savedFilter,
    filterModel,
    setFilterModel,
    storageKey,
    onUnselectSavedFilter,
    tableFilters,
    getSavedFilterDisabledReason,
  ])

  return (
    <SavedFilterContext.Provider
      value={{
        savedFilter: savedFilter ? { ...savedFilter, deletedAt: null } : null,
        isLoadingSavedFilter,
        reference,
        onUnselectSavedFilter,
        getSavedFilterDisabledReason,
      }}
    >
      {children}
    </SavedFilterContext.Provider>
  )
}
